import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Settings|Roles|Edit|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Core,
	portal: 'SP',
};

type LocNameStart = 'SPSettingsRolesEdit';
export type SPSettingsRolesEditLocNames =
	| `${LocNameStart}NotificationPermissionRequired`
	| `${LocNameStart}NotificationRoleNotFound`
	| `${LocNameStart}NotificationRoleRenameError`
	| `${LocNameStart}NotificationEditSuccess`
	| `${LocNameStart}ContentNoRoles`
	| `${LocNameStart}ButtonCreateNewRole`
	| `${LocNameStart}TitleAddPermission`
	| `${LocNameStart}ButtonCancelAdd`
	| `${LocNameStart}ButtonSaveAdd`
	| `${LocNameStart}TitlePermissions`
	| `${LocNameStart}TitleRemovePermissions`
	| `${LocNameStart}ButtonDeleteRole`
	| `${LocNameStart}ButtonRenameRole`
	| `${LocNameStart}ButtonCopyRole`
	| `${LocNameStart}ButtonRemovePermissions`
	| `${LocNameStart}ButtonAddPermissions`
	| `${LocNameStart}ButtonCancelRemove`
	| `${LocNameStart}ButtonSaveRemove`
	| `${LocNameStart}TitleCriteria`
	| `${LocNameStart}NotificationCriteriaCreated`
	| `${LocNameStart}NotificationCriteriaDeleted`;

export const spSettingsRolesEditLocs: LocDefs<SPSettingsRolesEditLocNames> = {
	SPSettingsRolesEditNotificationPermissionRequired: {
		defaultValue: 'At least one permission is required',
		description: 'Notification when trying to create a role without any permissions selected',
		displayPath: `${pathStart}Permission Required Notification`,
		displayType: 'Notification',
		legacyName: 'Core/Settings.Roles.Notification.PermissionsRequired',
		...sharedProperties,
	},
	SPSettingsRolesEditNotificationRoleNotFound: {
		defaultValue: 'Role not found',
		description: 'Notification when a role is not found in the system',
		displayPath: `${pathStart}Role not found`,
		displayType: 'Notification',
		legacyName: 'Core/Settings.Roles.Notification.NotFound',
		...sharedProperties,
	},
	SPSettingsRolesEditNotificationRoleRenameError: {
		defaultValue: 'There was a problem renaming the role',
		description: 'Notification when there was an error renaming a role',
		displayPath: `${pathStart}Role rename error`,
		displayType: 'Notification',
		legacyName: 'Core/Settings.Roles.NotificationMessage.RenameError',
		...sharedProperties,
	},
	SPSettingsRolesEditNotificationEditSuccess: {
		defaultValue: 'Changes saved',
		description: 'Notification when a role is successfully edited',
		displayPath: `${pathStart}Role rename success`,
		displayType: 'Notification',
		legacyName: 'Core/Settings.Roles.NotificationMessage.RenameSuccess',
		...sharedProperties,
	},
	SPSettingsRolesEditContentNoRoles: {
		defaultValue: 'There are currently no roles set up.',
		description: 'Message displayed when there are no roles in the system',
		displayPath: `${pathStart}No Roles`,
		displayType: 'Content',
		legacyName: 'Core/Settings.Roles.Content.NoRoles',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonCreateNewRole: {
		defaultValue: 'Create New Role',
		description: 'Button to create a new role',
		displayPath: `${pathStart}Create New Role`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.CreateRole',
		...sharedProperties,
	},
	SPSettingsRolesEditTitleAddPermission: {
		defaultValue: 'Select Permissions to Add',
		description: 'Add permission title',
		displayPath: `${pathStart}Add Permission Title`,
		displayType: 'Title',
		legacyName: 'Core/Settings.Roles.Title.AddPermissions',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonCancelAdd: {
		defaultValue: 'Cancel',
		description: 'Cancel button for adding permissions',
		displayPath: `${pathStart}Cancel Add Permission`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.CancelAdd',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonSaveAdd: {
		defaultValue: 'Add Selected Permissions to Role',
		description: 'Add button for adding permissions',
		displayPath: `${pathStart}Add Permission Button`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.SaveAdd',
		...sharedProperties,
	},
	SPSettingsRolesEditTitlePermissions: {
		defaultValue: 'Permissions',
		description: 'Permissions title',
		displayPath: `${pathStart}Permissions`,
		displayType: 'Title',
		legacyName: 'Core/Settings.Roles.Title.Permissions',
		...sharedProperties,
	},
	SPSettingsRolesEditTitleRemovePermissions: {
		defaultValue: 'Select Permissions to Remove',
		description: 'Select Permissions to Remove title',
		displayPath: `${pathStart}Remove Permissions`,
		displayType: 'Title',
		legacyName: 'Core/Settings.Roles.Title.RemovePermissions',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonDeleteRole: {
		defaultValue: 'Delete Role',
		description: 'Delete Role button',
		displayPath: `${pathStart}Delete Role`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.DeleteRole',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonRenameRole: {
		defaultValue: 'Rename Role',
		description: 'Rename Role button',
		displayPath: `${pathStart}Rename Role`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.RenameRole',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonCopyRole: {
		defaultValue: 'Copy Role',
		description: 'Copy Role button',
		displayPath: `${pathStart}Copy Role`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.CopyRole',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonRemovePermissions: {
		defaultValue: 'Remove Permissions',
		description: 'Remove Permissions button',
		displayPath: `${pathStart}Remove Permissions`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.Remove',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonAddPermissions: {
		defaultValue: 'Add Permissions',
		description: 'Add Permissions button',
		displayPath: `${pathStart}Add Permissions`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.Add',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonCancelRemove: {
		defaultValue: 'Cancel',
		description: 'Cancel Remove Permissions button',
		displayPath: `${pathStart}Cancel Remove Permissions`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.CancelRemove',
		...sharedProperties,
	},
	SPSettingsRolesEditButtonSaveRemove: {
		defaultValue: 'Remove Selected Permissions from Role',
		description: 'Save Remove Permissions button',
		displayPath: `${pathStart}Save Remove Permissions`,
		displayType: 'Button',
		legacyName: 'Core/Settings.Roles.Button.SaveRemove',
		...sharedProperties,
	},
	SPSettingsRolesEditTitleCriteria: {
		defaultValue: 'Criteria',
		description: 'Criteria section title',
		displayPath: `${pathStart}Criteria section title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPSettingsRolesEditNotificationCriteriaCreated: {
		defaultValue: 'Criteria created',
		description: 'Notification saying the criteria was created.',
		displayPath: `${pathStart}Criteria created`,
		displayType: 'Notification',
		...sharedProperties,
	},
	SPSettingsRolesEditNotificationCriteriaDeleted: {
		defaultValue: 'Criteria deleted',
		description: 'Notification saying the criteria was deleted.',
		displayPath: `${pathStart}Criteria deleted`,
		displayType: 'Notification',
		...sharedProperties,
	},
};

export type SPSettingsRolesEditLocType = LocMap<SPSettingsRolesEditLocNames>;
